import { useEffect, useState } from "react";

// Routes: /c/<channel>, /c/<channel>/m/<message> (the permalink form),
// /search, /settings/<page> (one's own), /admin/<page> (admins'), /tasks, /fleet,
// /workflows, /inbox, /unreads, /threads, /huddles, /files, /saved, /scheduled, /drafts, /new, /people, /emoji, /help, /agents/<agent>/traces
// /traces/<trace> and /artifacts/<artifact>. Older addresses move (see moved).
export interface Route {
  channel?: string;
  message?: string;
  search?: boolean;
  // settings is a page of one's own settings, admin one of the workspace's
  // administration: "" when the address names none.
  settings?: string;
  admin?: string;
  tasks?: boolean;
  fleet?: boolean;
  workflows?: boolean;
  inbox?: boolean;
  unreads?: boolean;
  threads?: boolean;
  huddles?: boolean;
  files?: boolean;
  saved?: boolean;
  scheduled?: boolean;
  drafts?: boolean;
  emoji?: boolean;
  help?: boolean;
  compose?: boolean;
  people?: boolean;
  // thread is a thread to open in the channel: /c/<channel>/t/<root>.
  thread?: string;
  traceAgent?: string;
  trace?: string;
  artifact?: string;
}

export function parse(path: string): Route {
  const th = path.match(/^\/c\/([0-9a-f-]{36})\/t\/([0-9a-f-]{36})/);
  if (th) return { channel: th[1], thread: th[2] };
  const m = path.match(/^\/c\/([0-9a-f-]{36})(?:\/m\/([0-9a-f-]{36}))?/);
  if (m) return { channel: m[1], message: m[2] };
  const a = path.match(/^\/agents\/([0-9a-f-]{36})\/traces/);
  if (a) return { traceAgent: a[1] };
  const art = path.match(/^\/artifacts\/([0-9a-f-]{36})/);
  if (art) return { artifact: art[1] };
  const t = path.match(/^\/traces\/([0-9a-f-]{36})/);
  if (t) return { trace: t[1] };
  if (path.startsWith("/search")) return { search: true };
  const own = path.match(/^\/settings(?:\/([a-z-]+))?\/?$/);
  if (own) return { settings: own[1] ?? "" };
  const adm = path.match(/^\/admin(?:\/([a-z-]+))?\/?$/);
  if (adm) return { admin: adm[1] ?? "" };
  if (path.startsWith("/tasks")) return { tasks: true };
  if (path.startsWith("/fleet")) return { fleet: true };
  if (path.startsWith("/workflows")) return { workflows: true };
  if (path.startsWith("/inbox")) return { inbox: true };
  if (path.startsWith("/unreads")) return { unreads: true };
  if (path.startsWith("/threads")) return { threads: true };
  if (path.startsWith("/huddles")) return { huddles: true };
  if (path.startsWith("/files")) return { files: true };
  if (path.startsWith("/saved")) return { saved: true };
  if (path.startsWith("/scheduled")) return { scheduled: true };
  if (path.startsWith("/drafts")) return { drafts: true };
  if (path.startsWith("/emoji")) return { emoji: true };
  if (path.startsWith("/help")) return { help: true };
  if (path === "/new") return { compose: true };
  if (path.startsWith("/people")) return { people: true };
  return {};
}

// Where pages that were tabs, or stood on their own, are now: a page of
// Settings or of Administration. Old links and bookmarks still open them.
const settingsTabs: Record<string, string> = { general: "preferences", notifications: "notifications", agents: "agents", account: "account", security: "security" };
const adminTabs: Record<string, string> = { general: "workspace", people: "accounts", invites: "invites", retention: "retention", security: "security" };
const adminPages: Record<string, string> = { "/system": "system", "/health": "system", "/models": "models", "/hosted": "hosted", "/discovery": "discovery" };

// moved is the address an old one is now at, or null when it has not moved.
export function moved(path: string, hash: string): string | null {
  const tab = hash.replace(/^#/, "");
  if (path === "/settings" || path === "/settings/") return `/settings/${settingsTabs[tab] ?? "preferences"}`;
  if (path === "/account") return "/settings/account";
  if (path === "/profile") return "/settings/profile";
  if (path === "/admin" || path === "/admin/") return "/admin/workspace";
  if (path === "/admin/workspace" && tab) return `/admin/${adminTabs[tab] ?? "workspace"}`;
  const page = adminPages[path.replace(/\/$/, "")];
  if (page) return `/admin/${page}${hash}`;
  return null;
}

export function navigate(path: string) {
  history.pushState(null, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parse(location.pathname));
  useEffect(() => {
    const on = () => setRoute(parse(location.pathname));
    addEventListener("popstate", on);
    return () => removeEventListener("popstate", on);
  }, []);
  return route;
}
